// Apps → App Factory: every run that takes an app from the AI wizard to a
// publisher handoff link, the pools runs draw from, and the wizard to start a
// new one. The run does the rest by itself (lib/factory-runner.ts on the API);
// a person reviews the package once and steps in only when a step fails.
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useToastStore } from '@/store/toast';
import { useAuthStore } from '@/store/auth';
import { getAppOwners } from '@/api/apps';
import {
  listFactoryRuns, getFactoryRun, getFactoryPools, retryFactoryStep, skipFactoryDomain, cancelFactoryRun,
  listFactoryCandidates, createFactoryRunForApp, isClientRun,
  type FactoryRun, type FactoryStep, type FactoryStepKey, type FactoryStepStatus, type FactoryCandidate,
} from '@/api/factory';
import { getPublisherOptions } from '@/api/publishers';
import { AppWizardModal } from './apps-setup/AppWizardModal';
import { FactoryPublishOn, publishOnInput, type PublishOnValue } from './apps-setup/FactoryPublishOn';
import { FactoryDomainChoice, NO_DOMAIN_CHOICE, domainChoiceProblem, domainInput, type DomainChoiceValue } from './apps-setup/FactoryDomainChoice';
import { STEP_DOT as DOT, STEP_TEXT as TEXT, STEP_WORD as WORD, fmtDuration, fmtUnix } from './apps-setup/factory-ui';

const STEP_LABEL: Record<FactoryStepKey, string> = {
  meta: 'Meta', review: 'Review', firebase: 'Firebase', domain: 'Domain', policy: 'Privacy policy', loader: 'Loader', build: 'Build', handoff: 'Link',
  access: 'Console access', publish: 'Publication',
};
const RETRYABLE: FactoryStepKey[] = ['firebase', 'domain', 'policy', 'loader', 'build', 'handoff', 'access', 'publish'];

const STATUS_OPTIONS = [
  { value: '', label: 'All runs' },
  { value: 'running', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** What the run is on now, in a few words. */
function runHeadline(run: FactoryRun): { text: string; status: FactoryStepStatus } {
  if (run.status === 'cancelled') return { text: 'Cancelled', status: 'skipped' };
  if (run.status === 'done') return { text: isClientRun(run) ? 'Built — with the client' : 'Link issued', status: 'done' };
  const failed = run.steps.find((s) => s.status === 'failed');
  if (failed) return { text: `${STEP_LABEL[failed.key]} failed`, status: 'failed' };
  const you = run.steps.find((s) => s.status === 'needs_you');
  if (you) return { text: isClientRun(run) ? 'Waiting for the client’s review' : 'Waiting for your review', status: 'needs_you' };
  const running = run.steps.filter((s) => s.status === 'running').map((s) => STEP_LABEL[s.key]);
  if (running.length) return { text: running.join(', '), status: 'running' };
  const waiting = run.steps.find((s) => s.status === 'waiting');
  return { text: waiting ? `${STEP_LABEL[waiting.key]}: ${waiting.detail ?? 'waiting'}` : '—', status: 'waiting' };
}

function StepDots({ steps }: { steps: FactoryStep[] }) {
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s) => (
        <span
          key={s.key}
          title={`${STEP_LABEL[s.key]}: ${WORD[s.status]}${s.detail ? ` — ${s.detail}` : ''}`}
          className={`w-3 h-3 rounded-full border-2 ${DOT[s.status]}`}
        />
      ))}
    </div>
  );
}

export function AppFactory() {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const admin = useAuthStore((s) => s.admin);
  const isTechnical = admin?.role === 'super_admin' || !!admin?.addons?.includes('developer');
  const [params, setParams] = useSearchParams();
  const openRunId = params.get('run');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  // The wizard: a new run, or the review of an existing one.
  const [wizard, setWizard] = useState<{ runId?: string; jobId?: string } | null>(null);
  const [ownerAdminId, setOwnerAdminId] = useState('');
  const [existingOpen, setExistingOpen] = useState(false);

  const runsQ = useQuery({
    queryKey: ['factory-runs', status],
    queryFn: () => listFactoryRuns(status || undefined),
    refetchInterval: 10_000,
  });
  const poolsQ = useQuery({ queryKey: ['factory-pools'], queryFn: getFactoryPools, refetchInterval: 30_000 });
  const owners = useQuery({ queryKey: ['admin-users', 'app-owners'], queryFn: getAppOwners, enabled: isTechnical });

  const runs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = runsQ.data ?? [];
    if (!needle) return all;
    return all.filter((r) => [r.name, r.bundle_id, r.publisher_name ?? (isClientRun(r) ? 'client run' : ''), r.domain]
      .some((v) => (v ?? '').toLowerCase().includes(needle)));
  }, [runsQ.data, q]);

  const openRun = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('run', id); else next.delete('run');
    setParams(next, { replace: true });
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['factory-runs'] });
    void qc.invalidateQueries({ queryKey: ['factory-run'] });
    void qc.invalidateQueries({ queryKey: ['factory-pools'] });
  };

  const pools = poolsQ.data;
  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-content">App Factory</h1>
        <p className="text-content-muted text-sm mt-1 max-w-3xl">
          Pick a publisher and a brand, check the AI package once — the run makes the Firebase project, buys the domain (where the stub’s repo type wants one),
          writes the privacy policy, draws the loader screen, builds the AAB and APK and issues the handoff link by itself. An app created
          earlier can go through the same run with “Existing app”.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <PoolCard
          label="Firebase profiles"
          value={pools ? `${pools.firebase.free_slots} free slots` : '…'}
          hint={pools ? `${pools.firebase.profiles} profiles · ${pools.firebase.busy} projects in progress` : ''}
          tone={!pools ? 'muted' : pools.firebase.free_slots === 0 ? 'bad' : pools.firebase.free_slots < 5 ? 'warn' : 'ok'}
          link={{ to: '/browser-profiles', label: 'Browser Profiles' }}
        />
        <PoolCard
          label="Domains"
          value={pools ? (pools.domains.enabled ? 'Purchase on' : 'Purchase off') : '…'}
          hint={pools?.domains.reason ?? 'Bought when the app is created'}
          tone={!pools ? 'muted' : pools.domains.enabled ? 'ok' : 'warn'}
        />
        <PoolCard
          label="Builds"
          value={pools ? (pools.builds.enabled ? 'Ready' : 'No GitHub token') : '…'}
          hint="AAB for the publisher, APK for checking"
          tone={!pools ? 'muted' : pools.builds.enabled ? 'ok' : 'bad'}
        />
        <PoolCard
          label="Runs"
          value={pools ? `${pools.runs.running} in progress` : '…'}
          hint={pools ? `${pools.runs.awaiting_review} waiting for review` : ''}
          tone={!pools ? 'muted' : pools.runs.awaiting_review > 0 ? 'warn' : 'ok'}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="flex gap-2 flex-1">
          <input className="input sm:max-w-xs" placeholder="Search app, bundle, publisher, domain…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={status} options={STATUS_OPTIONS} onChange={setStatus} className="w-40" />
        </div>
        {isTechnical && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setExistingOpen(true)} title="Finish an app that already exists: Firebase, domain, policy, builds, link">
              + Existing app
            </Button>
            <Button onClick={() => setWizard({})}>+ New app</Button>
          </div>
        )}
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">App</th>
              <th className="text-left p-3 font-medium">Publisher</th>
              <th className="text-left p-3 font-medium">Steps</th>
              <th className="text-left p-3 font-medium">Now</th>
              <th className="text-left p-3 font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.isLoading && <tr><td colSpan={5} className="p-6 text-center text-content-subtle">Loading…</td></tr>}
            {runsQ.error && <tr><td colSpan={5} className="p-6 text-center text-danger">{(runsQ.error as Error).message}</td></tr>}
            {!runsQ.isLoading && !runsQ.error && runs.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-content-subtle">No runs yet — press “New app”.</td></tr>
            )}
            {runs.map((run) => {
              const head = runHeadline(run);
              return (
                <tr key={run.id} onClick={() => openRun(run.id)} className="border-t border-border hover:bg-surface-muted/40 cursor-pointer">
                  <td className="p-3 align-middle">
                    <div className="flex items-center gap-3 min-w-0">
                      {run.icon_url
                        ? <img src={run.icon_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-border shrink-0" />
                        : <div className="w-9 h-9 rounded-lg bg-surface-muted border border-border shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-medium text-content truncate">{run.name ?? 'Generating…'}</div>
                        <div className="text-xs text-content-subtle truncate">{run.bundle_id ?? fmtUnix(run.created_at)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-content">
                    {isClientRun(run)
                      ? <span className="text-[11px] px-1.5 py-0.5 rounded border border-primary/40 text-primary">Client · {run.client_name ?? '—'}</span>
                      : (run.publisher_name ?? '—')}
                  </td>
                  <td className="p-3 align-middle"><StepDots steps={run.steps} /></td>
                  <td className={`p-3 align-middle ${TEXT[head.status]}`}>{head.text}</td>
                  <td className="p-3 align-middle text-content-subtle whitespace-nowrap">{fmtDuration(run.created_at, run.finished_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openRunId && (
        <RunPanel
          runId={openRunId}
          canSteer={isTechnical}
          onClose={() => openRun(null)}
          onReview={(run) => setWizard({ runId: run.id, jobId: run.wizard_job_id ?? undefined })}
          onChanged={refresh}
          toast={toast}
        />
      )}

      {existingOpen && (
        <ExistingAppModal
          onClose={() => setExistingOpen(false)}
          onStarted={(runId) => { setExistingOpen(false); refresh(); openRun(runId); toast('Run started — it fills in what the app lacks'); }}
        />
      )}

      <AppWizardModal
        isOpen={!!wizard}
        onClose={() => { setWizard(null); refresh(); }}
        onUseDraft={() => {}}
        isTechnical={isTechnical}
        ownerAdminId={ownerAdminId}
        owners={(owners.data ?? []).map((u) => ({ value: u.id, label: u.name ? `${u.name} · ${u.email}` : u.email }))}
        onOwnerChange={setOwnerAdminId}
        onCreated={() => refresh()}
        onFinished={() => {}}
        factory={wizard ? {
          runId: wizard.runId,
          jobId: wizard.jobId,
          onRunStarted: () => refresh(),
          onApproved: (runId) => {
            toast('Approved — the run builds and hands it off now');
            setWizard(null);
            openRun(runId);
            refresh();
          },
        } : undefined}
      />
    </div>
  );
}

/** What an existing app still lacks, as short tags; done pieces are muted. */
function CandidateTags({ c }: { c: FactoryCandidate }) {
  const tags: [string, boolean][] = [
    ['Firebase', c.has_firebase],
    [c.domain ?? 'Domain', !!c.domain],
    ['Policy', c.has_policy],
    [c.builds ? `${c.builds} build${c.builds === 1 ? '' : 's'}` : 'Build', c.builds > 0],
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(([label, has]) => (
        <span
          key={label}
          className={`text-[11px] px-1.5 py-0.5 rounded border ${has ? 'border-border text-content-subtle' : 'border-warning/50 text-warning'}`}
          title={has ? 'Already there' : 'The run makes it'}
        >
          {has ? '✓ ' : ''}{label}
        </span>
      ))}
    </div>
  );
}

function ExistingAppModal({ onClose, onStarted }: { onClose: () => void; onStarted: (runId: string) => void }) {
  const toast = useToastStore((s) => s.push);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<FactoryCandidate | null>(null);
  const [publisherId, setPublisherId] = useState('');
  const [publishUsd, setPublishUsd] = useState('');
  const [rentUsd, setRentUsd] = useState('');
  const [send, setSend] = useState(true);
  const [publishOn, setPublishOn] = useState<PublishOnValue | null>(null);
  const [domainChoice, setDomainChoice] = useState<DomainChoiceValue>(NO_DOMAIN_CHOICE);

  const candidates = useQuery({ queryKey: ['factory-candidates', q.trim()], queryFn: () => listFactoryCandidates(q.trim()) });
  const publishers = useQuery({ queryKey: ['publisher-options'], queryFn: getPublisherOptions });
  const publisher = publishers.data?.find((p) => p.id === publisherId);
  const pickPublisher = (id: string) => {
    setPublisherId(id);
    const p = publishers.data?.find((item) => item.id === id);
    if (p) { setPublishUsd(String(p.publish_cents / 100)); setRentUsd(String(p.rent_cents / 100)); }
  };
  const cents = (v: string) => Math.round(Number(v || 0) * 100);
  const start = useMutation({
    mutationFn: () => createFactoryRunForApp({
      app_id: picked!.id, publisher_id: publisherId,
      publish_cents: cents(publishUsd), rent_cents: cents(rentUsd), send_to_publisher: send,
      ...publishOnInput(publishOn),
      ...domainInput(picked!.domain_rule, domainChoice),
    }),
    onSuccess: (r) => onStarted(r.run_id),
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Could not start the run'),
  });
  const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-content';
  const domainProblem = picked
    ? domainChoiceProblem(picked.domain_rule, domainChoice, { publishing: !!publishOn?.accountId, hasPolicy: picked.has_policy, existingDomain: picked.domain })
    : null;

  return (
    <Modal isOpen onClose={onClose} title="Finish an existing app" size="2xl">
      <div className="space-y-4">
        <p className="text-sm text-content-subtle">
          The run takes the app as it is (name, icon, texts, screenshots) and makes what it lacks: the Firebase project,
          the domain where its repo type wants one, the privacy policy, the loader screen, the AAB and APK, then the handoff link. What the app already has stays.
        </p>

        <div>
          <input className="input w-full" placeholder="Search by name or package…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <div className="mt-2 max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border">
            {candidates.isLoading && <div className="p-3 text-sm text-content-subtle">Loading…</div>}
            {candidates.error && <div className="p-3 text-sm text-danger">{(candidates.error as Error).message}</div>}
            {!candidates.isLoading && (candidates.data ?? []).length === 0 && (
              <div className="p-3 text-sm text-content-subtle">No Android apps without a run in progress match.</div>
            )}
            {(candidates.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setPicked(c)}
                className={`w-full text-left flex items-center gap-3 p-2.5 hover:bg-surface-muted/50 ${picked?.id === c.id ? 'bg-primary/5' : ''}`}
              >
                {c.icon_url
                  ? <img src={c.icon_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-border shrink-0" />
                  : <div className="w-9 h-9 rounded-lg bg-surface-muted border border-border shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium truncate ${picked?.id === c.id ? 'text-primary' : 'text-content'}`}>{c.name}</div>
                  <div className="text-xs text-content-subtle truncate">
                    {c.bundle_id}{c.last_run_at ? ` · had a run ${fmtUnix(c.last_run_at)}` : ''}
                  </div>
                </div>
                <CandidateTags c={c} />
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-content-muted mb-1">Publisher</label>
            <Select
              value={publisherId}
              onChange={pickPublisher}
              searchable
              placeholder={publishers.isLoading ? 'Loading…' : 'Who will publish it'}
              options={(publishers.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-content-muted mb-1">Publication, $</label>
            <input type="number" min={0} step="0.01" value={publishUsd} onChange={(e) => setPublishUsd(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-content-muted mb-1">Rent per week, $</label>
            <input type="number" min={0} step="0.01" value={rentUsd} onChange={(e) => setRentUsd(e.target.value)} className={inputClass} />
          </div>
        </div>
        <label className="flex items-start gap-2 text-xs text-content-subtle">
          <input type="checkbox" className="mt-0.5" checked={send} onChange={(e) => setSend(e.target.checked)} />
          <span>Send the link to the publisher's chat when it is ready{publisher && !publisher.has_chat ? ' (this publisher has no chat)' : ''}</span>
        </label>
        <FactoryPublishOn
          value={publishOn}
          onChange={(v) => { if (v && !publishOn) setSend(false); setPublishOn(v); }}
        />
        {picked && (
          <FactoryDomainChoice
            rule={picked.domain_rule}
            typeName={picked.type_name}
            value={domainChoice}
            onChange={setDomainChoice}
            publishing={!!publishOn?.accountId}
            hasPolicy={picked.has_policy}
            existingDomain={picked.domain}
          />
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            disabled={!picked || !publisherId || !!domainProblem || (!!publishOn && (!publishOn.accountId || !publishOn.templateId))}
            title={domainProblem ?? undefined}
            loading={start.isPending}
            onClick={() => start.mutate()}
          >
            {picked ? `Start the run for ${picked.name}` : 'Pick an app'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PoolCard({ label, value, hint, tone, link }: {
  label: string; value: string; hint: string; tone: 'ok' | 'warn' | 'bad' | 'muted';
  link?: { to: string; label: string };
}) {
  const color = { ok: 'text-content', warn: 'text-warning', bad: 'text-danger', muted: 'text-content-subtle' }[tone];
  return (
    <div className="bg-surface border border-border rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-content-subtle mt-0.5 truncate" title={hint}>
        {hint}
        {link && <> · <Link to={link.to} className="text-primary hover:underline">{link.label}</Link></>}
      </div>
    </div>
  );
}

function RunPanel({ runId, canSteer, onClose, onReview, onChanged, toast }: {
  runId: string; canSteer: boolean; onClose: () => void; onReview: (run: FactoryRun) => void; onChanged: () => void;
  toast: (msg: string) => void;
}) {
  const runQ = useQuery({ queryKey: ['factory-run', runId], queryFn: () => getFactoryRun(runId), refetchInterval: 8_000 });
  const run = runQ.data;
  const [skipEmail, setSkipEmail] = useState('');
  const [skipping, setSkipping] = useState(false);

  const after = (msg: string) => ({
    onSuccess: () => { toast(msg); onChanged(); },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Failed'),
  });
  const retry = useMutation({ mutationFn: (step: FactoryStepKey) => retryFactoryStep(runId, step), ...after('Retrying') });
  const skip = useMutation({
    mutationFn: () => skipFactoryDomain(runId, skipEmail.trim()),
    ...after('Building on the shared host'),
  });
  const cancel = useMutation({ mutationFn: () => cancelFactoryRun(runId), ...after('Run cancelled') });

  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast(`${what} copied`); } catch { toast('Could not copy'); }
  };
  const publisherMessage = run?.handoff_url
    ? [`New app: ${run.name ?? ''}`, run.bundle_id ? `Package: ${run.bundle_id}` : '', run.handoff_url].filter(Boolean).join('\n')
    : '';
  const reviewStep = run?.steps.find((s) => s.key === 'review');

  return (
    <Modal isOpen onClose={onClose} title={run?.name ?? 'App Factory run'} size="2xl">
      {!run ? (
        <p className="text-sm text-content-subtle">{runQ.error ? (runQ.error as Error).message : 'Loading…'}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {run.icon_url && <img src={run.icon_url} alt="" className="w-12 h-12 rounded-xl object-cover border border-border" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-content">
                {run.bundle_id ? <code>{run.bundle_id}</code> : 'Not created yet'}
                {run.domain && <> · {run.domain}</>}
              </div>
              <div className="text-xs text-content-subtle">
                {isClientRun(run) ? (
                  <>Client run for {run.client_name ?? '—'} · the client reviews the package and submits the app for publishing itself{run.contact_email ? ` · support ${run.contact_email}` : ''}</>
                ) : (
                  <>
                    For {run.publisher_name ?? '—'} · ${((run.publish_cents ?? 0) / 100).toFixed(2)} + ${((run.rent_cents ?? 0) / 100).toFixed(2)}/week
                    {run.send_to_publisher ? (run.publisher_has_chat ? ' · link goes to their chat' : ' · publisher has no chat') : ' · link sent by hand'}
                  </>
                )}
                {' '}· started {fmtUnix(run.created_at)}
              </div>
              {run.play_account_id && (
                <div className="text-xs text-content-subtle">
                  Published by us on {run.play_account_name}
                  {run.shared_profile_name && <> · shared console, Firebase in profile {run.shared_profile_name}</>}
                  {run.publish_job_id && <> · <Link to="/play/publish" className="text-primary hover:underline">First publication</Link></>}
                </div>
              )}
            </div>
            {/* A client reviews and approves its own package; the app would otherwise be created as ours. */}
            {canSteer && !isClientRun(run) && run.status === 'running' && run.wizard_job_id && reviewStep && reviewStep.status !== 'waiting' && (
              <Button size="sm" variant={reviewStep.status === 'needs_you' ? 'primary' : 'secondary'} onClick={() => onReview(run)}>
                {reviewStep.status === 'done' ? 'Open the package' : 'Review'}
              </Button>
            )}
          </div>

          {run.handoff_url && (
            <div className="p-3 rounded-lg border border-success/40 bg-success-soft/40">
              <div className="text-xs uppercase tracking-wide text-content-subtle mb-1">Handoff link</div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs break-all flex-1 min-w-0">{run.handoff_url}</code>
                <Button size="sm" variant="secondary" onClick={() => copy(run.handoff_url!, 'Link')}>Copy link</Button>
                <Button size="sm" variant="secondary" onClick={() => copy(publisherMessage, 'Message')}>Copy message</Button>
              </div>
              {run.handoff_expires_at && <div className="text-xs text-content-subtle mt-1">Valid until {fmtUnix(run.handoff_expires_at)}</div>}
            </div>
          )}

          <ol className="space-y-2">
            {run.steps.map((s) => (
              <li key={s.key} className="flex items-start gap-3 p-2.5 rounded-lg border border-border">
                <span className={`mt-1 w-3 h-3 rounded-full border-2 shrink-0 ${DOT[s.status]}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-content">{STEP_LABEL[s.key]}</span>
                    <span className={`text-xs ${TEXT[s.status]}`}>{WORD[s.status]}</span>
                  </div>
                  {s.detail && <div className={`text-xs mt-0.5 break-words ${s.status === 'failed' ? 'text-danger' : 'text-content-subtle'}`}>{s.detail}</div>}
                  {canSteer && s.key === 'domain' && s.status === 'failed' && run.domain_skippable && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {skipping ? (
                        <>
                          <input
                            className="input text-xs py-1 w-64"
                            placeholder={run.console_gmail ? `Contact email (empty: ${run.console_gmail})` : 'Contact email for the privacy policy'}
                            value={skipEmail}
                            onChange={(e) => setSkipEmail(e.target.value)}
                          />
                          <Button size="sm" variant="secondary" disabled={!skipEmail.includes('@') && !run.console_gmail} loading={skip.isPending} onClick={() => skip.mutate()}>
                            Build without own domain
                          </Button>
                        </>
                      ) : (
                        <button type="button" className="text-xs text-content-subtle hover:text-content underline" onClick={() => setSkipping(true)}>
                          Skip — build on the shared host
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {canSteer && run.status === 'running' && s.status === 'failed' && RETRYABLE.includes(s.key) && (
                  <Button size="sm" variant="secondary" loading={retry.isPending && retry.variables === s.key} onClick={() => retry.mutate(s.key)}>
                    Retry
                  </Button>
                )}
                {/* The publisher says nothing yet: check as if they had pressed Access granted. */}
                {canSteer && run.status === 'running' && s.key === 'access' && s.status === 'waiting'
                  && (run.console_access_state === 'sent' || run.console_access_state === 'refused') && (
                  <Button size="sm" variant="secondary" loading={retry.isPending && retry.variables === s.key} onClick={() => retry.mutate(s.key)}>
                    Check now
                  </Button>
                )}
              </li>
            ))}
          </ol>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="flex flex-wrap gap-3 text-xs">
              {run.app_id && (
                <>
                  <Link to={`/apps-setup?tab=build&app=${run.app_id}`} className="text-primary hover:underline">Build tab</Link>
                  <Link to={`/apps-setup?tab=share&app=${run.app_id}`} className="text-primary hover:underline">Share tab</Link>
                </>
              )}
            </div>
            {canSteer && run.status === 'running' && (
              <button
                type="button"
                className="text-xs text-danger hover:underline"
                onClick={() => { if (confirm('Cancel this run? What it already made (app, Firebase project, domain, builds) stays.')) cancel.mutate(); }}
              >
                Cancel run
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
